import { Control } from "Controls/Control";
import { DisplayModes, CssClass } from "Enums";
import { ControlTemplate } from "Controls/Entities";
import { Navigation } from "Core";
import { ControlEventTypes } from "Core/Events/EventTypes";

/**
 * Control for showing links.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a LinkControl with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class Link extends Control {
    /**
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-link";

    /**
     * @hidden
     */
    protected defaultEditorElement = "<a />";

    protected isBlockElement = false;

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        if (this.controlDefinition.Caption && !this.controlDefinition.ValueColumnName) {
            this.jQueryElement.append(this.textBlockService.get(this.controlDefinition.Caption, this.textBlockCategory));
        }
        this.on(ControlEventTypes.Click, () => this.prepareClick());
        this.jQueryElement.on("click", () => this.trigger(ControlEventTypes.Click));
        this.jQueryElement.on("focusout", (e) => this.trigger(ControlEventTypes.LostFocus));
    }

    private prepareClick(): void {
        this.trigger(ControlEventTypes.CheckRequiredFields);
        this.trigger(ControlEventTypes.BeforeCommand);

        if (this.controlDefinition.ConfirmText) {
            let text = this.textBlockService.get(this.controlDefinition.ConfirmText, this.textBlockCategory);

            Navigation.confirmAsync(this.resolve(text), this.textBlockService.get("pageTitle", this.textBlockCategory)).then((execute) => {
                if (execute) {
                    this.executeClick();
                }
            });
        } else {
            this.executeClick();
        }
    }

    private executeClick() {
        if (this.controlDefinition.Script) {
            this.eventHandler.executeScript(this.resolve(this.controlDefinition.Script));
        }
        if (this.controlDefinition.Link) {
            let link = this.resolve(this.controlDefinition.Link) || this.controlDefinition.Link;
            if (this.controlDefinition.LinkMode) {
                Navigation.openWindow(link, this.controlDefinition.LinkMode);
            } else {
                Navigation.navigate(link);
            }
        }
        this.trigger(ControlEventTypes.AfterCommand);
    }

    /**
     * Not implemented for this control.
     * @hidden
     */
    public getValue(): string {
        return null;
    }

    /**
     * Not implemented for this control.
     * @param value
     * @hidden
     */
    public setValue(value: string) {}

    /**
     * Internal method for getting the template.
     * @param displayMode DisplayMode for control.
     * @param bindingField Field which the control is bound to.
     * @hidden
     */
    protected getTemplateInternal(displayMode: DisplayModes, bindingField: string): ControlTemplate {
        let controlTemplate = new ControlTemplate();

        let elem = $(this.defaultEditorElement);
        super.setDefaultEditorProperties(elem);
        elem.addClass(CssClass.link);

        // link with image
        if (this.controlDefinition.Icon && this.controlDefinition.Icon.indexOf(".") >= 0) {
            let imgElem = $("<img></img>");
            imgElem.attr("src", this.environment.ImagePath + this.controlDefinition.Icon);
            elem.append(imgElem);
        }
        // link with sprite class
        else if (this.controlDefinition.Icon) {
            elem.addClass("k-button k-button-icon");
            let span = $(`<span />`);
            span.addClass(this.controlDefinition.Icon);
            elem.append(span);
        } else if (this.controlDefinition.ValueColumnName) {
            if (displayMode == DisplayModes.Display) {
                elem.html(this.getDisplayTemplate(this.controlDefinition.ValueColumnName));
            } else {
                elem.attr("data-bind", "html: " + this.controlDefinition.ValueColumnName);
            }
        }
        controlTemplate.template = elem[0].outerHTML;
        controlTemplate.element = elem;
        controlTemplate.editable = false;
        return controlTemplate;
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        if (this.jQueryElement) {
            this.jQueryElement.off("click");
            this.jQueryElement.off("focusout");
        }
    }
}
